<script lang="ts" setup>
import { computed, ref, useAttrs } from 'vue';
import type { RouterLinkProps } from 'vue-router';

import { KaButton } from '@/kasten-components/components/KaButton';
import { buttonVariants } from '@/kasten-components/components/KaButton/button-variants';
import { KaLink } from '@/kasten-components/components/KaLink';
import type { MaybePromise } from '@/kasten-components/types';
import { cnMerge } from '@/utils/utils.js';

import { injectMultiStepFormContext, type Step } from './model';

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

const {
  onBack: onTriggerBack,
  isFirstStep,
  isLastStep,
  prevStep,
  currentStep,
} = injectMultiStepFormContext();

const props = defineProps<{
  onBack: (currentStep: Step, prevStep?: Step) => MaybePromise<boolean>;
  jumpToFirstStep?: boolean;
  /**
   * When provided and on the first step, renders the cancel button as a link
   * navigating to the given route instead of a button.
   */
  cancelTo?: RouterLinkProps;
}>();

const loading = ref(false);

const shouldRenderAsLink = computed(() => isFirstStep.value && props.cancelTo != null);

const handleClick = async () => {
  loading.value = true;
  if (await props.onBack(currentStep.value, prevStep.value)) {
    onTriggerBack();
  }
  loading.value = false;
};
</script>
<template>
  <KaLink
    v-if="shouldRenderAsLink"
    v-bind="{ ...attrs, ...cancelTo! }"
    :class="
      cnMerge(
        buttonVariants({ variant: 'default', size: 'base' }),
        'no-underline hover:no-underline max-sm:flex-grow',
      )
    "
    data-testid="multiStepBack"
  >
    <slot :loading="false" :last-step="isLastStep" :first-step="isFirstStep"> Cancel </slot>
    <span class="sr-only">, Cancel and leave the multi-step form.</span>
  </KaLink>
  <KaButton
    v-else
    v-bind="attrs"
    class="max-sm:flex-grow"
    :loading="loading"
    data-testid="multiStepBack"
    @click="handleClick"
  >
    <slot :loading="loading" :last-step="isLastStep" :first-step="isFirstStep">
      {{ isFirstStep ? 'Cancel' : 'Back' }}
    </slot>
    <span class="sr-only">
      <template v-if="isFirstStep">, Cancel and leave the multi-step form.</template>
      <template v-else>, Return to {{ prevStep?.name }}.</template>
    </span>
  </KaButton>
</template>
